import { Dispatch, useMemo, useReducer } from 'react';

import { Product, SubscriptionListEntry } from 'ggApp/types/graphql';

import useAction from '../../hooks/useAction';

import reducer, { defaultState } from './reducer';
import {
    Action,
    appendFilterItems as appendFilterItemsAction,
    AppendFilterItemsAction,
    deleteProduct as deleteProductAction,
    DeleteProductAction,
    PayloadWithPagination,
    setFilterItems as setFilterItemsAction,
    SetFilterItemsAction,
    setProduct as setProductAction,
    SetProductAction,
} from './actions';

export interface FilterActions {
    setFilterItems: (param: PayloadWithPagination) => void;
    appendFilterItems: (param: PayloadWithPagination) => void;
    setProduct: (param: Product) => void;
    deleteProduct: (param: undefined) => void;
}

export function useActions(dispatch: Dispatch<Action>): FilterActions {
    const setFilterItems = useAction<PayloadWithPagination, SetFilterItemsAction>(
        dispatch,
        setFilterItemsAction,
    );
    const setProduct = useAction<Product, SetProductAction>(dispatch, setProductAction);
    const deleteProduct = useAction<undefined, DeleteProductAction>(dispatch, deleteProductAction);
    const appendFilterItems = useAction<PayloadWithPagination, AppendFilterItemsAction>(
        dispatch,
        appendFilterItemsAction,
    );

    return useMemo(
        () => ({
            setFilterItems,
            setProduct,
            deleteProduct,
            appendFilterItems,
        }),
        [setFilterItems, setProduct, deleteProduct, appendFilterItems],
    );
}

function useFilterState(): SubscriptionsFilterState {
    const [state, dispatch] = useReducer(reducer, defaultState);
    const actions = useActions(dispatch);

    const filterState = useMemo(
        () => ({
            ...state,
            actions,
        }),
        [actions, state],
    );

    return filterState;
}

export default useFilterState;

export interface SubscriptionsFilterState {
    product?: Product;
    filterItems: SubscriptionListEntry[];
    actions: FilterActions;
}
